'use client';

import { cn } from "../../lib/utils";
import { useState } from 'react';

interface FlipTextProps {
  word?: string;
  className?: string;
}

type Mode = "replay" | "shuffle" | "reverse" | "drop";

interface Offset {
  x: number;
  y: number;
  r: number; // rotation
  s: number; // scale
}

// Generate a random offset for one character
const randomOffset = (): Offset => ({
  x: Math.round((Math.random() - 0.5) * 400),        // -200px .. 200px
  y: Math.round((Math.random() - 0.5) * 300),        // -150px .. 150px
  r: Math.round((Math.random() - 0.5) * 120),        // -60deg .. 60deg
  s: +(0.8 + Math.random() * 1.2).toFixed(2),        // 0.8 .. 2.0
});

export default function FlipTextReveal({
  word = "CHARTS TELL YOU THAT → TWINS SHOW YOU WHY",
  className = "",
}: FlipTextProps) {
  const [key, setKey] = useState(0);
  const [mode, setMode] = useState<Mode>("replay");
  const [offsets, setOffsets] = useState<Offset[]>([]);

  // WATCH A SHIFT REPLAY — standard staggered flip reveal
  const replay = () => {
    setMode("replay");
    setKey((prev) => prev + 1);
  };

  // RUN A WHAT-IF — letters scatter from NEW random positions on every click
  const shuffle = () => {
    setMode("shuffle");
    setOffsets(word.split("").map(() => randomOffset()));
    setKey((prev) => prev + 1);
  };

  // GO BACK IN TIME — reveal runs in reverse order (last char first)
  const reverse = () => {
    setMode("reverse");
    setKey((prev) => prev + 1);
  };

  // SEND A SPATIAL BRIEF — whole words drop in as blocks, one at a time
  const drop = () => {
    setMode("drop");
    setKey((prev) => prev + 1);
  };

  const actions: { label: string; fn: () => void; mode: Mode }[] = [
    { label: "Watch a Shift Replay", fn: replay, mode: "replay" },
    { label: "Run a What-If", fn: shuffle, mode: "shuffle" },
    { label: "Go Back in Time", fn: reverse, mode: "reverse" },
    { label: "Send a Spatial Brief", fn: drop, mode: "drop" },
  ];

  // Split into words once — spacing comes from flex gap, not space chars
  const words = word.split(" ");

  return (
    <div className={cn("flip-container", className)}>

      <div
        key={key}
        className={`text-wrapper mode-${mode}`}
        style={{ "--word-len": word.length } as React.CSSProperties}
      >
        <h1 className="title" aria-label={word}>
          {words.map((w, wi) => {
            // global index of this word's first char (word lengths + one space each)
            const wordStart = words
              .slice(0, wi)
              .reduce((acc, prev) => acc + prev.length + 1, 0);
            return (
              <span
                key={`key−word−{key}-word-key−word−{wi}`}
                className="word"
                style={{ "--word-index": wi } as React.CSSProperties}
                aria-hidden="true"
              >
                {w.split("").map((char, ci) => {
                  const o = offsets[wordStart + ci];
                  return (
                    <span
                      key={`key−{key}-key−{wordStart + ci}`}
                      className="char"
                      style={
                        {
                          "--index": wordStart + ci,
                          "--char-in-word": ci,
                          "--rand-x": o ? `${o.x}px` : "0px",
                          "--rand-y": o ? `${o.y}px` : "0px",
                          "--rand-r": o ? `${o.r}deg` : "0deg",
                          "--rand-s": o ? o.s : 1,
                        } as React.CSSProperties
                      }
                    >
                      {char}
                    </span>
                  );
                })}
              </span>
            );
          })}
        </h1>
      </div>

      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", justifyContent: "center" }}>
        {actions.map(({ label, fn, mode: m }) => (
          <button
            key={label}
            className={`replay-button ${mode === m ? "active" : ""}`}
            onClick={fn}
          >
            <span className="btn-text">{label}</span>
          </button>
        ))}
      </div>

      <style jsx>{`
        /* --- INVERTED THEME VARIABLES --- */
        .flip-container {
          /* Light Mode (Default): Component is BLACK, Text is WHITE */
          --bg-color: #09090b;
          --text-color: #ffffff;

          /* Button styling */
          --btn-bg: #27272a;
          --btn-text: #ffffff;
          --btn-border: #3f3f46;
          --btn-hover: #52525b;
        }

        @media (prefers-color-scheme: dark) {
          .flip-container {
            /* Dark Mode: Component is WHITE, Text is BLACK */
            --bg-color: #ffffff;
            --text-color: #09090b;

            --btn-bg: #f4f4f5;
            --btn-text: #18181b;
            --btn-border: #e4e4e7;
            --btn-hover: #d4d4d8;
          }
        }

        /* Manual .dark class override */
        :global(.dark) .flip-container {
          --bg-color: #ffffff;
          --text-color: #09090b;
          --btn-bg: #f4f4f5;
          --btn-text: #18181b;
          --btn-border: #e4e4e7;
          --btn-hover: #d4d4d8;
        }

        /* --- Layout --- */
        .flip-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          background-color: var(--bg-color);
          color: var(--text-color);
          border-radius: 16px;
          overflow: hidden;
          min-height: 350px;
          width: 100%;
          transition: background-color 0.4s ease, color 0.4s ease;

          /* 3D Stage */
          perspective: 800px;
          box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.1);
        }

        /* --- Typography --- */
        .title {
          font-size: 3rem;
          font-weight: 900;
          margin: 0;
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.35em; /* ← THE WORD SPACING: comes from here, not space chars */
          line-height: 1;
          text-transform: uppercase;
          letter-spacing: -0.04em;
          transform-style: preserve-3d;
        }

        .word {
          display: inline-flex;
          transform-style: preserve-3d;
        }

        /* --- Character base state (hidden until animation starts) --- */
        .char {
          display: inline-block;
          color: var(--text-color);
          transform-origin: bottom center;

          opacity: 0;
          will-change: transform, opacity;
        }

        /* ============================================================
           MODE 1 — REPLAY: staggered 3D flip (original behavior)
           ============================================================ */
        .mode-replay .char {
          transform: rotateX(-90deg) translateY(20px);
          animation: flip-up 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
          animation-delay: calc(0.06s * var(--index));
        }

        /* ============================================================
           MODE 2 — SHUFFLE ("Run a What-If"): letters fly in from
           RANDOM positions (new on every click), then assemble
           into the sentence
           ============================================================ */
        .mode-shuffle .char {
          animation: shuffle-in 0.8s cubic-bezier(0.22, 1, 0.36, 1) forwards;
          animation-delay: calc(0.02s * var(--index));
        }

        /* ============================================================
           MODE 3 — REVERSE ("Go Back in Time"): reveal counts down
           from the end of the sentence, flipping the other way
           ============================================================ */
        .mode-reverse .char {
          transform: rotateX(90deg) translateY(20px);
          animation: flip-down 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
          animation-delay: calc(0.06s * (var(--word-len) - var(--index)));
        }

        /* ============================================================
           MODE 4 — DROP ("Send a Spatial Brief"): each word drops as
           ONE block — a chip/tile with a background — no per-char
           staggering. Whole "CHARTS" falls, then "TELL", then "YOU"…
           ============================================================ */
        .mode-drop .word {
          display: inline-flex;
          padding: 0.15em 0.35em;
          border-radius: 0.18em;
          background-color: var(--btn-bg);
          border: 1px solid var(--btn-border);
          opacity: 0;
          transform-origin: top center;
          animation: block-drop 0.6s cubic-bezier(0.34, 1.3, 0.64, 1) forwards;
          animation-delay: calc(0.18s * var(--word-index));
          will-change: transform, opacity;
        }

        /* In drop mode the chars themselves are static and fully visible —
           the WORD is the animated unit */
        .mode-drop .char {
          opacity: 1;
          animation: none;
          transform: none;
        }

        /* --- Buttons --- */
        .replay-button {
          margin-top: 3.5rem;
          padding: 0.8rem 2rem;
          background-color: var(--btn-bg);
          color: var(--btn-text);
          border: 1px solid var(--btn-border);
          border-radius: 99px;
          font-weight: 600;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s ease;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          opacity: 0.55;
        }

        .replay-button:hover {
          background-color: var(--btn-hover);
          transform: scale(1.05);
          opacity: 1;
        }

        .replay-button:active {
          transform: scale(0.95);
        }

        /* Active button = the mode currently playing */
        .replay-button.active {
          opacity: 1;
          background-color: var(--text-color);
          color: var(--bg-color);
          border-color: var(--text-color);
        }

        /* --- Keyframes --- */
        @keyframes flip-up {
          0% {
            opacity: 0;
            transform: rotateX(-90deg) translateY(40px);
          }
          100% {
            opacity: 1;
            transform: rotateX(0deg) translateY(0);
          }
        }

        @keyframes flip-down {
          0% {
            opacity: 0;
            transform: rotateX(90deg) translateY(40px);
          }
          100% {
            opacity: 1;
            transform: rotateX(0deg) translateY(0);
          }
        }

        @keyframes shuffle-in {
          0% {
            opacity: 0;
            transform: translate(var(--rand-x), var(--rand-y))
              rotate(var(--rand-r))
              scale(var(--rand-s));
          }
          60% {
            opacity: 1;
          }
          100% {
            opacity: 1;
            transform: translate(0, 0) rotate(0deg) scale(1);
          }
        }

        @keyframes block-drop {
          0% {
            opacity: 0;
            transform: translateY(-120px) scale(1.15);
          }
          70% {
            opacity: 1;
            transform: translateY(8px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Responsive Text Sizing */
        @media (max-width: 768px) {
          .title {
            font-size: 1.6rem;
          }
          .replay-button {
            margin-top: 2.5rem;
            padding: 0.7rem 1.4rem;
            font-size: 0.75rem;
          }
        }

        /* Reduced motion: show final state instantly for ALL modes */
        @media (prefers-reduced-motion: reduce) {
          .char,
          .mode-drop .word {
            opacity: 1 !important;
            transform: none !important;
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}
